import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { grpplayerlist, playerpresent } from "../components/funtionsBD/Mainservice";
import { isPlayerAccount, readActiveHomeRole } from "../utils/homeRole";
import { resolveViewerPlayerId } from "../utils/trainingRoomAccess";
import { normalizeStoredGroupInfo } from "../utils/liveTrainingGroupInfo";
import {
  getJoinableGroupLiveTrainingContext,
  persistLiveTrainingSession,
  resolveGroupIdsForLiveTraining,
} from "./useLiveTrainingRedirect";
import { requestLiveFullscreen } from "./useLiveFullscreen";

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

/**
 * Player bottom-nav Live Training tab: always visible. Tap joins a group or
 * own session that starts within 15 minutes (or is already on).
 */
export function usePlayerLiveTrainingNav({ enabled = true } = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [sessionState, setSessionState] = useState(null);
  const [joining, setJoining] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setSessionState(null);
      return;
    }
    const user = readUser();
    const homeRole = readActiveHomeRole(user);
    if (!isPlayerAccount(user, { homeRole })) {
      setSessionState(null);
      return;
    }
    try {
      const groupIds = await resolveGroupIdsForLiveTraining(user);
      const ctx = await getJoinableGroupLiveTrainingContext({ user, groupIds });
      setSessionState(ctx?.session || null);
    } catch {
      setSessionState(null);
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 60000);
    return () => window.clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    const onStatus = () => refresh();
    window.addEventListener("smarttt-session-live-status", onStatus);
    window.addEventListener("smarttt-session-changed", onStatus);
    window.addEventListener("smarttt-home-role-changed", onStatus);
    return () => {
      window.removeEventListener("smarttt-session-live-status", onStatus);
      window.removeEventListener("smarttt-session-changed", onStatus);
      window.removeEventListener("smarttt-home-role-changed", onStatus);
    };
  }, [refresh]);

  const join = useCallback(async () => {
    const session = sessionState;
    if (!session?.ts_id || joining) return { ok: false };
    const user = readUser();
    if (user && user.emailVerification !== true && user.is_demo_walkin !== true) {
      return { ok: false, error: "emailVerificationRequiredBody" };
    }

    requestLiveFullscreen(undefined, { hideBrowserChrome: true });
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem("grpinf") || "null");
    } catch {
      stored = null;
    }
    persistLiveTrainingSession(
      session,
      normalizeStoredGroupInfo(stored, session)
    );
    // Navigate in this click/pointer tick so fullscreen is not lost while
    // presence waits on the network.
    if (location.pathname !== "/live-training") {
      navigate("/live-training");
    }

    setJoining(true);
    try {
      const grId = session.gr_id || stored?.groupDetails?.gr_id || stored?.gr_id;
      let playerId = user.player_id != null ? String(user.player_id) : null;
      let alreadyPresent = false;
      if (grId) {
        try {
          const res = await grpplayerlist({ ts_id: session.ts_id, gr_id: grId });
          const roster = res?.data?.data || [];
          playerId = resolveViewerPlayerId(user, roster) || playerId;
          alreadyPresent = roster.some(
            (p) =>
              playerId &&
              String(p?.player_id) === String(playerId) &&
              p.presensee === true
          );
        } catch {
          /* still try presence with user.player_id */
        }
      }

      const idForPresence = playerId || user.user_id;
      if (!alreadyPresent && idForPresence) {
        await playerpresent({
          ts_id: session.ts_id,
          gr_id: grId,
          ts_present: true,
          player_id: idForPresence,
        });
      }

      return { ok: true };
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        null;
      return { ok: false, error: msg || "liveTrainingJoinError" };
    } finally {
      setJoining(false);
    }
  }, [sessionState, joining, location.pathname, navigate]);

  return {
    visible: Boolean(enabled && sessionState?.ts_id),
    sessionState,
    joining,
    join,
    refresh,
  };
}
